/**
 * Host bridge — abstracts the three runtimes that can mount a widget:
 *
 *   1. ChatGPT Apps SDK            — `window.openai` global injected by the host
 *   2. mcp-ui clients (Claude etc) — message-passing over postMessage
 *   3. Demo / standalone           — in-memory shim used by the /demo route
 *
 * Widget code reads `host.data`, calls `host.callTool(...)`, subscribes to
 * `host.subscribe(event, fn)`. The widget never branches on which client it's
 * running in; this module is the single seam between Apps SDK and mcp-ui.
 *
 * Data delivery model (read carefully — the design is load-bearing):
 *
 *   - Apps SDK injects `window.openai.toolOutput` BEFORE the script runs. We
 *     also accept `window.openai._meta.widgetData` as the rich payload (Apps
 *     SDK convention: `structuredContent` for model, `_meta` for widget).
 *   - mcp-ui inlines the resource as HTML, with `<script>window.__widgetData__
 *     = {...}</script>` baked in by the MCP server at resource-read time.
 *   - The demo route writes `window.__widgetData__` programmatically before
 *     React mounts.
 *
 * Theme/locale/displayMode follow the same precedence: Apps SDK globals win,
 * mcp-ui falls back to navigator + matchMedia, demo can force via URL params.
 */

declare global {
  interface Window {
    openai?: AppsSdkGlobal;
    __widgetData__?: unknown;
  }
}

type AppsSdkTheme = 'light' | 'dark';
type DisplayMode = 'inline' | 'pip' | 'fullscreen';

interface AppsSdkGlobal {
  toolOutput?: unknown;
  toolInput?: unknown;
  widgetState?: unknown;
  theme?: AppsSdkTheme;
  locale?: string;
  displayMode?: DisplayMode;
  maxHeight?: number;
  _meta?: { widgetData?: unknown; 'openai/widgetData'?: unknown };
  callTool?: (name: string, args: Record<string, unknown>) => Promise<unknown>;
  setWidgetState?: (state: unknown) => Promise<void> | void;
  requestDisplayMode?: (mode: DisplayMode) => Promise<unknown>;
  sendFollowupTurn?: (input: { prompt: string }) => Promise<void> | void;
}

export type HostKind = 'apps-sdk' | 'mcp-ui' | 'standalone';

export type HostEvent = 'theme' | 'locale' | 'displayMode' | 'data' | 'widgetState';

export interface Host {
  readonly kind: HostKind;

  /** Rich widget payload — the data structuredContent leaves out. */
  readonly data: unknown;
  /** Last persisted widget state (filters, selected row…). */
  readonly widgetState: unknown;
  readonly theme: AppsSdkTheme;
  readonly locale: string;
  readonly displayMode: DisplayMode;

  /**
   * Invoke a follow-up MCP tool. Both runtimes treat this as a real tool call —
   * the resulting tool response flows back into the conversation, not into the
   * widget. If the widget needs to react to the new data, mount a fresh
   * instance and let the host re-inject `data`.
   */
  callTool(name: string, args: Record<string, unknown>): Promise<unknown>;

  /** Persist widget state across re-mounts in this conversation. */
  setWidgetState(state: unknown): Promise<void>;

  /** Request a display-mode change (inline → pip → fullscreen). */
  requestDisplayMode(mode: DisplayMode): Promise<void>;

  /**
   * Subscribe to host updates. Returns an unsubscribe fn. The host re-fires
   * the event with the new value whenever ChatGPT / mcp-ui pushes one in.
   */
  subscribe(event: HostEvent, listener: (value: unknown) => void): () => void;
}

type Listeners = { [E in HostEvent]?: Set<(value: unknown) => void> };

/* ────────────────────────────────────────────────────────────────────────── */
/* Apps SDK host                                                              */
/* ────────────────────────────────────────────────────────────────────────── */

class AppsSdkHost implements Host {
  readonly kind: HostKind = 'apps-sdk';
  private listeners: Listeners = {};
  private localState: unknown;

  constructor(private readonly api: AppsSdkGlobal) {
    this.localState = api.widgetState;

    // Apps SDK fires a `openai:set_globals` event on `window` whenever any
    // global (theme, locale, displayMode, toolOutput, widgetState…) changes.
    // We just rebroadcast under our own event names.
    window.addEventListener('openai:set_globals', this.onSetGlobals);
  }

  private onSetGlobals = (e: Event) => {
    const detail = (e as CustomEvent<{ globals?: Record<string, unknown> }>).detail ?? {};
    const globals = detail.globals ?? {};
    if ('theme' in globals) this.emit('theme', globals.theme);
    if ('locale' in globals) this.emit('locale', globals.locale);
    if ('displayMode' in globals) this.emit('displayMode', globals.displayMode);
    if ('toolOutput' in globals || '_meta' in globals) this.emit('data', this.data);
    if ('widgetState' in globals) {
      this.localState = globals.widgetState;
      this.emit('widgetState', globals.widgetState);
    }
  };

  get data(): unknown {
    // Apps SDK forwards the tool response's `structuredContent` as
    // `window.openai.toolOutput`. That's the documented + working channel
    // (verified against pizzaz_server_node). We also accept the legacy
    // `_meta.widgetData` / `_meta["openai/widgetData"]` keys for older
    // responses.
    return this.api.toolOutput ?? this.api._meta?.['openai/widgetData'] ?? this.api._meta?.widgetData;
  }

  get widgetState(): unknown {
    return this.localState ?? this.api.widgetState;
  }

  get theme(): AppsSdkTheme {
    return this.api.theme ?? 'dark';
  }

  get locale(): string {
    return this.api.locale ?? navigator.language ?? 'en-US';
  }

  get displayMode(): DisplayMode {
    return this.api.displayMode ?? 'inline';
  }

  async callTool(name: string, args: Record<string, unknown>): Promise<unknown> {
    if (!this.api.callTool) {
      throw new Error('Apps SDK callTool is not available in this runtime');
    }
    return this.api.callTool(name, args);
  }

  async setWidgetState(state: unknown): Promise<void> {
    this.localState = state;
    if (this.api.setWidgetState) await this.api.setWidgetState(state);
  }

  async requestDisplayMode(mode: DisplayMode): Promise<void> {
    if (this.api.requestDisplayMode) await this.api.requestDisplayMode(mode);
  }

  subscribe(event: HostEvent, listener: (value: unknown) => void): () => void {
    const set = (this.listeners[event] ??= new Set());
    set.add(listener);
    return () => set.delete(listener);
  }

  private emit(event: HostEvent, value: unknown) {
    this.listeners[event]?.forEach(fn => fn(value));
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* mcp-ui host (Claude.ai, Goose, MCP Inspector, etc.)                        */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * mcp-ui defines a small postMessage protocol between the widget iframe and
 * the host page. The shape we implement here matches the published spec
 * (https://mcp-ui.dev) at the time of writing:
 *
 *   widget → host: { type: 'tool',   payload: { toolName, params }      }
 *   widget → host: { type: 'prompt', payload: { prompt }                }
 *   widget → host: { type: 'intent', payload: { intent, params }        }
 *   widget → host: { type: 'notify', payload: { message }               }
 *   host   → widget: { type: 'ui-message-received', messageId, payload  }
 *   host   → widget: { type: 'ui-size-change-requested', ... }
 */
type McpUiOutgoing =
  | { type: 'tool'; messageId: string; payload: { toolName: string; params: Record<string, unknown> } }
  | { type: 'prompt'; messageId: string; payload: { prompt: string } };

class McpUiHost implements Host {
  readonly kind: HostKind = 'mcp-ui';
  private listeners: Listeners = {};
  private localState: unknown;
  private pending = new Map<string, { resolve: (v: unknown) => void; reject: (e: unknown) => void }>();
  private themeValue: AppsSdkTheme;

  constructor(private readonly bakedData: unknown) {
    this.themeValue = preferredTheme();
    try {
      this.localState = readLocalStateFromStorage();
    } catch {
      this.localState = undefined;
    }

    window.addEventListener('message', this.onMessage);

    // Re-fire theme on system change for hosts that don't push it.
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (mq) {
      const handle = () => {
        this.themeValue = mq.matches ? 'dark' : 'light';
        this.emit('theme', this.themeValue);
      };
      // matchMedia exposes addEventListener in evergreen browsers, addListener
      // in Safari ≤ 14. We support both because Claude and Inspector use Edge
      // and embedded WebKit respectively.
      if ('addEventListener' in mq) mq.addEventListener('change', handle);
      else (mq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(handle);
    }
  }

  private onMessage = (event: MessageEvent) => {
    const msg = event.data;
    if (!msg || typeof msg !== 'object') return;
    // Host pushes data updates (rare for static widgets, but supported).
    if (msg.type === 'ui-data-pushed' && 'payload' in msg) {
      (window as Window).__widgetData__ = msg.payload;
      this.emit('data', msg.payload);
      return;
    }
    if (msg.type === 'ui-theme-changed' && 'theme' in msg) {
      const t = msg.theme === 'light' ? 'light' : 'dark';
      this.themeValue = t;
      this.emit('theme', t);
      return;
    }
    // Reply to a previous tool/prompt call.
    if (typeof msg.messageId === 'string' && this.pending.has(msg.messageId)) {
      const slot = this.pending.get(msg.messageId)!;
      this.pending.delete(msg.messageId);
      if (msg.type === 'ui-message-response-error') slot.reject(msg.payload);
      else slot.resolve(msg.payload);
    }
  };

  get data(): unknown {
    return this.bakedData ?? (window as Window).__widgetData__;
  }

  get widgetState(): unknown {
    return this.localState;
  }

  get theme(): AppsSdkTheme {
    return this.themeValue;
  }

  get locale(): string {
    return navigator.language ?? 'en-US';
  }

  get displayMode(): DisplayMode {
    return 'inline';
  }

  async callTool(name: string, args: Record<string, unknown>): Promise<unknown> {
    return this.send({
      type: 'tool',
      messageId: randomMessageId(),
      payload: { toolName: name, params: args },
    });
  }

  async setWidgetState(state: unknown): Promise<void> {
    this.localState = state;
    try {
      writeLocalStateToStorage(state);
    } catch {
      /* sessionStorage can throw in sandboxed iframes; the worst case is the
       * state doesn't persist across re-mounts in this client. The next host
       * push will refill it anyway. */
    }
  }

  async requestDisplayMode(_mode: DisplayMode): Promise<void> {
    /* mcp-ui has no display-mode equivalent at the time of writing. */
  }

  subscribe(event: HostEvent, listener: (value: unknown) => void): () => void {
    const set = (this.listeners[event] ??= new Set());
    set.add(listener);
    return () => set.delete(listener);
  }

  private send(msg: McpUiOutgoing): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.pending.set(msg.messageId, { resolve, reject });
      try {
        window.parent.postMessage(msg, '*');
      } catch (err) {
        this.pending.delete(msg.messageId);
        reject(err);
      }
      // Host might not implement message-response; cap the wait so we don't
      // leak listeners forever.
      setTimeout(() => {
        if (this.pending.has(msg.messageId)) {
          this.pending.delete(msg.messageId);
          resolve(undefined);
        }
      }, 30_000);
    });
  }

  private emit(event: HostEvent, value: unknown) {
    this.listeners[event]?.forEach(fn => fn(value));
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Standalone host (demo + dev)                                               */
/* ────────────────────────────────────────────────────────────────────────── */

class StandaloneHost implements Host {
  readonly kind: HostKind = 'standalone';
  private listeners: Listeners = {};
  private state: unknown;
  private themeValue: AppsSdkTheme;

  constructor(private readonly bakedData: unknown) {
    this.themeValue = preferredTheme();
  }

  get data(): unknown {
    return this.bakedData ?? (window as Window).__widgetData__;
  }
  get widgetState(): unknown {
    return this.state;
  }
  get theme(): AppsSdkTheme {
    return this.themeValue;
  }
  get locale(): string {
    return navigator.language ?? 'en-US';
  }
  get displayMode(): DisplayMode {
    return 'inline';
  }

  async callTool(name: string, args: Record<string, unknown>): Promise<unknown> {
    // eslint-disable-next-line no-console
    console.info('[widget] callTool (standalone, no-op)', name, args);
    return undefined;
  }

  async setWidgetState(state: unknown): Promise<void> {
    this.state = state;
    this.emit('widgetState', state);
  }

  async requestDisplayMode(_mode: DisplayMode): Promise<void> {
    /* no-op */
  }

  subscribe(event: HostEvent, listener: (value: unknown) => void): () => void {
    const set = (this.listeners[event] ??= new Set());
    set.add(listener);
    return () => set.delete(listener);
  }

  /** Test/demo affordance — let the demo route flip the theme. */
  setTheme(theme: AppsSdkTheme) {
    this.themeValue = theme;
    this.emit('theme', theme);
  }

  private emit(event: HostEvent, value: unknown) {
    this.listeners[event]?.forEach(fn => fn(value));
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Detection + entry point                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Build the right host for whichever runtime the widget is mounted in.
 *
 * Detection order (don't reorder — Apps SDK and mcp-ui can co-exist when
 * embedded in tools like Goose that wrap a ChatGPT-style global):
 *
 *   1. If `window.openai` exists, assume Apps SDK.
 *   2. Else, if we're inside an iframe (window.parent !== window), assume
 *      mcp-ui — even if no data has arrived yet the parent will postMessage
 *      it in once we mount.
 *   3. Else, standalone.
 *
 * Demo data: routes can pass `data` through to seed StandaloneHost; otherwise
 * the host reads from `window.__widgetData__` (which the inlined `<script>`
 * tag from the MCP server populates) on the fly.
 */
export function createHost(seedData?: unknown): Host {
  if (typeof window === 'undefined') {
    return new StandaloneHost(seedData);
  }
  if (window.openai) {
    return new AppsSdkHost(window.openai);
  }
  const inIframe = (() => {
    try {
      return window.parent !== window;
    } catch {
      // SecurityError on cross-origin parent access means we're in an iframe.
      return true;
    }
  })();
  if (inIframe) {
    return new McpUiHost(seedData ?? window.__widgetData__);
  }
  return new StandaloneHost(seedData);
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

function preferredTheme(): AppsSdkTheme {
  if (typeof window === 'undefined') return 'dark';
  const params = new URLSearchParams(window.location.search);
  const forced = params.get('theme');
  if (forced === 'light' || forced === 'dark') return forced;
  if (window.openai?.theme === 'light' || window.openai?.theme === 'dark') {
    return window.openai.theme;
  }
  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light';
  return 'dark';
}

const STORAGE_KEY = '42a-mcp-widget-state';

function readLocalStateFromStorage(): unknown {
  if (typeof sessionStorage === 'undefined') return undefined;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  return raw ? JSON.parse(raw) : undefined;
}

function writeLocalStateToStorage(state: unknown) {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function randomMessageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `m_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

/** React-friendly helper: build a host once per mount. */
export function detectHostKind(): HostKind {
  if (typeof window === 'undefined') return 'standalone';
  if (window.openai) return 'apps-sdk';
  try {
    if (window.parent !== window) return 'mcp-ui';
  } catch {
    return 'mcp-ui';
  }
  return 'standalone';
}
